// ============================================================
// DISC — Algoritmo de Scoring (Likert)
// 24 afirmações, 6 por fator, escala 1-5
// Scores independentes por fator (sem dependência forced-choice)
// ============================================================

import { COMBINED_PROFILES } from "@/components/disc/discProfileData";
import { QUESTIONS } from "@/components/disc/discData";

const FACTORS = ["D", "I", "S", "C"];

// ── Special Situations Detection ────────────────────────────

function detectSpecialSituations(percentages) {
  const situations = [];

  const pctValues = FACTORS.map((f) => percentages[f]);

  const allHigh = pctValues.every((v) => v >= 65);
  const allLow = pctValues.every((v) => v <= 35);
  const allCenter = pctValues.every((v) => v >= 40 && v <= 60);
  const hasExtremes = pctValues.some((v) => v >= 75) && pctValues.some((v) => v <= 25);

  if (allHigh) {
    situations.push({
      key: "overshift",
      title: "Overshift detectado",
      description:
        "Todos os seus fatores estão elevados simultaneamente. Isso pode indicar uma tendência a querer apresentar múltiplos estilos ao mesmo tempo, o que pode gerar inconsistência na percepção dos outros. Foque nos seus 1-2 fatores mais naturais para criar uma identidade comportamental mais clara.",
      severity: "attention",
    });
  }

  if (allLow) {
    situations.push({
      key: "undershift",
      title: "Undershift detectado",
      description:
        "Todos os seus fatores estão abaixo da média. Isso pode indicar um momento de introspecção ou incerteza sobre como se posicionar. Não significa falta de capacidade — pode refletir um momento de transição profissional onde você está reavaliando seu estilo.",
      severity: "attention",
    });
  }

  if (allCenter) {
    situations.push({
      key: "comprimido",
      title: "Perfil comprimido",
      description:
        "Seus fatores estão todos próximos do centro, sem um estilo comportamental claramente dominante. Isso sugere versatilidade — você se adapta a diferentes contextos — mas pode dificultar que outros identifiquem rapidamente seus pontos fortes. Considere investir em desenvolver deliberadamente 1-2 fatores que mais se alinham com seus objetivos de carreira.",
      severity: "info",
    });
  }

  if (hasExtremes) {
    situations.push({
      key: "estendido",
      title: "Perfil estendido",
      description:
        "Seus fatores apresentam extremos opostos — alguns muito altos e outros muito baixos. Isso cria um perfil com contrastes fortes, o que pode ser uma vantagem em papéis que exigem essa combinação específica, mas pode gerar comportamentos que parecem contraditórios para quem convive com você.",
      severity: "info",
    });
  }

  return situations;
}

// ── Principal ────────────────────────────────────────────────

export function calculateDiscResult(answers) {
  // 1. Build factor-to-questions map
  const factorQuestions = {};
  for (const f of FACTORS) {
    factorQuestions[f] = QUESTIONS.filter((q) => q.factor === f).map((q) => q.id);
  }

  // 2. Sum Likert values per factor (range: 6-30 per factor)
  const rawScores = {};
  for (const f of FACTORS) {
    rawScores[f] = factorQuestions[f].reduce((sum, qId) => {
      const value = answers[qId];
      return sum + (typeof value === "number" ? value : 3);
    }, 0);
  }

  // 3. Normalize to 0-100% (independent per factor)
  // Min = 6 (all 1s), Max = 30 (all 5s), Range = 24
  const percentages = {};
  for (const f of FACTORS) {
    percentages[f] = Math.round(((rawScores[f] - 6) / 24) * 100);
  }

  // 4. Determine primary and secondary factors
  // Tie-breaking: when percentages are equal, use raw scores.
  // If still tied, use factor variance (how spread the individual item
  // responses are — higher variance = more distinctive = wins tie).
  // Final fallback: alphabetical order (D > I > S > C is arbitrary but deterministic).
  const sorted = [...FACTORS].sort((a, b) => {
    const pctDiff = percentages[b] - percentages[a];
    if (pctDiff !== 0) return pctDiff;
    const rawDiff = rawScores[b] - rawScores[a];
    if (rawDiff !== 0) return rawDiff;
    // Compute variance for each factor — higher variance = more distinct pattern
    const varianceA = factorQuestions[a].reduce((s, qId) => {
      const v = typeof answers[qId] === "number" ? answers[qId] : 3;
      const mean = rawScores[a] / 6;
      return s + (v - mean) ** 2;
    }, 0);
    const varianceB = factorQuestions[b].reduce((s, qId) => {
      const v = typeof answers[qId] === "number" ? answers[qId] : 3;
      const mean = rawScores[b] / 6;
      return s + (v - mean) ** 2;
    }, 0);
    if (varianceB !== varianceA) return varianceB - varianceA;
    return a.localeCompare(b);
  });

  const primaryFactor = sorted[0];
  const secondaryFactor = sorted[1];

  // 5. Combined profile
  // If gap ≤ 10 percentage points → balanced (both uppercase, e.g. "DI")
  // If gap > 10 → dominant primary (primary upper, secondary lower, e.g. "Di")
  const gap = percentages[primaryFactor] - percentages[secondaryFactor];
  const combinedProfile = gap <= 10
    ? `${primaryFactor}${secondaryFactor}`
    : `${primaryFactor}${secondaryFactor.toLowerCase()}`;

  // 6. Profile data lookup
  const profileData = COMBINED_PROFILES[combinedProfile]
    || COMBINED_PROFILES[`${primaryFactor}${secondaryFactor.toLowerCase()}`]
    || null;
  const profileLabel = profileData?.label || `Perfil ${primaryFactor}`;

  // 7. Special situations
  const specialSituations = detectSpecialSituations(percentages);

  return {
    rawScores,
    percentages,
    primaryFactor,
    secondaryFactor,
    combinedProfile,
    profileLabel,
    profileData,
    specialSituations,
  };
}
